import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as LogoutIcon } from '../../assets/icons/logout.svg';
import {
  ReactComponent as RoamingActiveIcon,
  ReactComponent as RoamingIcon,
} from '../../assets/icons/sidebar/roaming-active.svg';
import {
  ReactComponent as ChargersActiveIcon,
  ReactComponent as ChargersIcon,
} from '../../assets/icons/sidebar/sidebar-chargers.svg';
import {
  ReactComponent as CPOActiveIcon,
  ReactComponent as CPOIcon,
} from '../../assets/icons/sidebar/sidebar-cpo.svg';
import {
  ReactComponent as EMSPActiveIcon,
  ReactComponent as EMSPIcon,
} from '../../assets/icons/sidebar/sidebar-emsp.svg';
import {
  ReactComponent as UsersActiveIcon,
  ReactComponent as UsersIcon,
} from '../../assets/icons/sidebar/sidebar-users.svg';
import { useAppBranding } from '../../config/branding';
// import config from '../../config/config';
// import { useBranding } from '../../contexts/BrandingContext';
import { PortalType } from '../../config/enum';
import { usePortal } from '../../contexts/PortalContext/PortalContext';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { removeCookie } from '../../shared-functions/Cookies';
// import getFirstSubdomain from '../../shared-functions/DomainsplitFunction';
import { getUserType, platformPermissionCheck } from '../../shared-functions/JWTDocodeFunction';
import { getLocalStorageItem, removeLocalStorageItem } from '../../shared-functions/LocalStorage';
import authApi from '../../utils/api/v2/auth';

import ChargerMenus from './components/ChargerMenu';
import CPOMenus from './components/CPOMenu';
import EMSPMenus from './components/EMSPMenu';
import PartnerMenus from './components/PartnerMenu';
import RoamingMenu from './components/RoamingMenu';
import UserMenus from './components/UserMenu';

/**
 * ChevronDown component
 * @returns {any} ChevronDown component
 */
function ChevronDown() {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 1L5 5L9 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * ChevronRight component
 * @returns {any} ChevronRight component
 */
function ChevronRight() {
  return (
    <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 9L5 5L1 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface MenuItem {
  id: string;
  label: string;
  url?: string;
  svgIcon?: React.ReactNode;
  activeSvgIcon?: React.ReactNode;
  allow: boolean;
  permissionKey?: string | string[];
  children?: MenuItem[];
  isOpen?: boolean;
  activeMatch?: string;
}

interface PortalMenus extends MenuItem {}

/**
 * Sidebar component
 * @returns {any} Sidebar component
 */
function Sidebar() {
  const { t, i18n } = useTranslation('sharedComponent');
  const sidebarReference = useRef<HTMLDivElement>(null);
  const [expandSidebar, setExpandSidebar] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { images: brandImage } = useAppBranding();

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [appVersion, setAppVersion] = useState<string>('');
  const { portalType, activePortal, setActivePortal } = usePortal();
  const userType = getUserType();

  /**
   * List of all portal menus with their respective icon, permission checks, and visibility criteria.
   */
  const portalMenus: PortalMenus[] = React.useMemo(() => {
    return [
      {
        id: PortalType.NETWORKS,
        label: 'network',
        url: '/ev-admin/cpo',
        svgIcon: <CPOIcon />,
        activeSvgIcon: <CPOActiveIcon />,
        allow:
          userType === PortalType.APP
            ? platformPermissionCheck(PortalType.NETWORKS)
            : userType === PortalType.NETWORKS,
      },

      {
        id: PortalType.MOBILITY,
        label: 'mobility',
        url: '/ev-admin/emsp',
        svgIcon: <EMSPIcon />,
        activeSvgIcon: <EMSPActiveIcon />,
        allow:
          userType === PortalType.APP
            ? platformPermissionCheck(PortalType.MOBILITY)
            : userType === PortalType.MOBILITY,
      },

      {
        id: PortalType.CHARGER,
        label: 'deviceHub',
        url: '/ev-admin/charger',
        svgIcon: <ChargersIcon />,
        activeSvgIcon: <ChargersActiveIcon />,
        allow:
          userType === PortalType.APP
            ? platformPermissionCheck(PortalType.CHARGER)
            : userType === PortalType.CHARGER,
      },

      {
        id: PortalType.ROAMING,
        label: 'roaming',
        url: '/ev-admin/ocpi',
        svgIcon: <RoamingIcon />,
        activeSvgIcon: <RoamingActiveIcon />,
        allow:
          userType === PortalType.APP
            ? platformPermissionCheck(PortalType.ROAMING)
            : userType === PortalType.ROAMING,
      },
      {
        id: PortalType.PARTNER,
        label: 'partner',
        url: '/ev-admin/partner',
        svgIcon: <UsersIcon />,
        activeSvgIcon: <UsersActiveIcon />,
        allow:
          userType === PortalType.APP
            ? platformPermissionCheck(PortalType.PARTNER)
            : userType === PortalType.PARTNER,
      },

      {
        id: PortalType.USERS,
        label: 'users',
        url: '/ev-admin/users',
        svgIcon: <UsersIcon />,
        activeSvgIcon: <UsersActiveIcon />,
        allow:
          userType === PortalType.APP
            ? platformPermissionCheck(PortalType.USERS)
            : (userType === PortalType.NETWORKS ||
                userType === PortalType.MOBILITY ||
                userType === PortalType.CHARGER ||
                userType === PortalType.PARTNER ||
                userType === PortalType.NETWORKS) &&
              platformPermissionCheck(PortalType.USERS),
      },
    ];
  }, [userType]);

  /**
   * Filtered list of portals that are allowed for the current user.
   */
  const allowedPortals = React.useMemo(() => {
    return portalMenus.filter((portal) => portal.allow);
  }, [portalMenus]);

  /**
   * Get all menu items
   * @param {any} currentPortal - Active portal type
   * @returns {any} Array of menu items
   */
  const getAllMenuItems = (currentPortal: string): MenuItem[] => {
    switch (currentPortal) {
      case PortalType.NETWORKS:
        return CPOMenus();

      case PortalType.MOBILITY:
        return EMSPMenus();

      case PortalType.PARTNER:
        return PartnerMenus();

      case PortalType.CHARGER:
        return ChargerMenus();

      case PortalType.USERS:
        return UserMenus();

      case PortalType.ROAMING:
        return RoamingMenu();
      case PortalType.APP:
        switch (activePortal) {
          case PortalType.NETWORKS:
            return CPOMenus();

          case PortalType.MOBILITY:
            return EMSPMenus();

          case PortalType.CHARGER:
            return ChargerMenus();

          case PortalType.PARTNER:
            return PartnerMenus();

          case PortalType.USERS:
            return UserMenus();

          case PortalType.ROAMING:
            return RoamingMenu();

          default:
            return [];
        }

      default:
        return [];
    }
  };

  /**
   * Deep filter menu items based on 'allow' property and Portals config in enum.ts
   * @param {any} items - The items
   */
  const filterAllowedMenus = (items: MenuItem[]): MenuItem[] => {
    return items
      .map((item) => {
        if (item.children) {
          const filteredChildren = filterAllowedMenus(item.children);
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter((item) => {
        if (!item.allow) return false;

        if (item.children?.length === 0) return false;

        return true;
      });
  };

  /**
   * Get menus by portal
   * @param {any} portal - Portal name
   * @returns {any} Menu items
   */
  const getMenusByPortal = (portal: string): MenuItem[] => {
    const allItems = getAllMenuItems(portal);
    // Filter recursively checking Portals config from enum.ts
    return filterAllowedMenus(allItems);
  };
  useEffect(() => {
    setMenuItems(getMenusByPortal(activePortal));
  }, [activePortal]);

  useEffect(() => {
    const { pathname, search, state } = location;
    const querySource = new URLSearchParams(search).get('source');
    const sourceParameter = (state as { source?: string })?.source || querySource;

    // Detect target portal from current URL path
    let pathPortal: string | undefined;
    if (pathname.includes('/user-management') || pathname.includes('/users')) {
      pathPortal = PortalType.USERS;
    } else if (pathname.includes('/ev-admin/cpo')) {
      pathPortal = PortalType.NETWORKS;
    } else if (pathname.includes('/ev-admin/emsp')) {
      pathPortal = PortalType.MOBILITY;
    } else if (
      (pathname.includes('/ev-admin/charger') &&
        !pathname.includes('charger-management') &&
        !pathname.includes('charger-expanded')) ||
      pathname.includes('/ev-device-hub') ||
      pathname.includes('/connector') ||
      pathname.includes('/brand') ||
      pathname.includes('/model')
    ) {
      pathPortal = PortalType.CHARGER;
    } else if (pathname.includes('/ev-admin/ocpi')) {
      pathPortal = PortalType.ROAMING;
    } else if (pathname.includes('/ev-admin/partner')) {
      pathPortal = PortalType.PARTNER;
    }

    if (
      !pathPortal &&
      (pathname.includes('charger-management') || pathname.includes('charger-expanded')) &&
      sourceParameter
    ) {
      if (sourceParameter.includes('/cpo') || sourceParameter.includes('/operations/')) {
        pathPortal = PortalType.NETWORKS;
      } else if (sourceParameter.includes('/emsp')) {
        pathPortal = PortalType.MOBILITY;
      } else if (sourceParameter.includes('/ev-device-hub')) {
        pathPortal = PortalType.CHARGER;
      } else if (sourceParameter.includes('/ocpi')) {
        pathPortal = PortalType.ROAMING;
      } else if (sourceParameter.includes('/partner')) {
        pathPortal = PortalType.PARTNER;
      }
    }

    if (pathPortal && allowedPortals.some((p) => p.id === pathPortal)) {
      if (activePortal !== pathPortal) {
        setActivePortal(pathPortal);
      }
      return;
    }

    // Keep current activePortal if it is already valid and allowed for this user
    const isCurrentActiveAllowed =
      activePortal && allowedPortals.some((p) => p.id === activePortal);
    if (isCurrentActiveAllowed) {
      return;
    }

    const hostname = globalThis?.location?.hostname;

    // Direct hostname matching for non-app subdomains
    if (
      hostname.includes(PortalType.PARTNER) &&
      allowedPortals.some((p) => p.id === PortalType.PARTNER)
    ) {
      if (activePortal !== PortalType.PARTNER) setActivePortal(PortalType.PARTNER);
      return;
    }
    if (
      hostname.includes(PortalType.MOBILITY) &&
      allowedPortals.some((p) => p.id === PortalType.MOBILITY)
    ) {
      if (activePortal !== PortalType.MOBILITY) setActivePortal(PortalType.MOBILITY);
      return;
    }
    if (
      hostname.includes(PortalType.CHARGER) &&
      allowedPortals.some((p) => p.id === PortalType.CHARGER)
    ) {
      if (activePortal !== PortalType.CHARGER) setActivePortal(PortalType.CHARGER);
      return;
    }
    if (
      hostname.includes(PortalType.NETWORKS) &&
      allowedPortals.some((p) => p.id === PortalType.NETWORKS)
    ) {
      if (activePortal !== PortalType.NETWORKS) setActivePortal(PortalType.NETWORKS);
      return;
    }
    if (
      hostname.includes(PortalType.ROAMING) &&
      allowedPortals.some((p) => p.id === PortalType.ROAMING)
    ) {
      if (activePortal !== PortalType.ROAMING) setActivePortal(PortalType.ROAMING);
      return;
    }

    // Default portal selection fallback for APP portal or general hostname (e.g. localhost)
    const savedPortal = localStorage.getItem('activePortal');
    const isSavedAllowed = savedPortal && allowedPortals.some((p) => p.id === savedPortal);

    if (isSavedAllowed && savedPortal) {
      if (activePortal !== savedPortal) {
        setActivePortal(savedPortal);
      }
    } else if (allowedPortals.length > 0) {
      const firstAllowedPortal = allowedPortals[0]?.id;
      if (firstAllowedPortal && activePortal !== firstAllowedPortal) {
        setActivePortal(firstAllowedPortal);
      }
    }
  }, [
    allowedPortals,
    activePortal,
    location.pathname,
    location.search,
    location.state,
    setActivePortal,
  ]);

  // Helper to strictly close all menus (used when navigating from floating popup)
  /**
   * Close all menus
   * @param {any} items - Menu items
   * @returns {any} Closed menu items
   */
  function closeAllMenus(items: MenuItem[]): MenuItem[] {
    return items.map((item) => ({
      ...item,
      isOpen: false,
      children: item.children ? closeAllMenus(item.children) : undefined,
    }));
  }

  /**
   * Handle navigation
   * @param {any} url - Target URL
   */
  function handleNavigate(url?: string) {
    if (url) {
      if (!expandSidebar) {
        setMenuItems((previous) => closeAllMenus(previous));
      }
      navigate(url);
    }
  }

  /**
   * Handle portal switch and navigate to first available module page
   * @param {any} portalId - Target portal ID
   */
  const handlePortalSwitch = (portalId: string) => {
    setActivePortal(portalId);
    const menus = getMenusByPortal(portalId);

    /**
     * Find first allowed URL
     * @param {any} items - Menu items
     * @returns {any} First allowed URL
     */
    const findFirstAllowedUrl = (items: MenuItem[]): string | undefined =>
      items.reduce<string | undefined>((accumulator, item) => {
        if (accumulator) return accumulator;
        if (!item.allow) return undefined;
        return item.url || (item.children ? findFirstAllowedUrl(item.children) : undefined);
      }, undefined);

    const targetUrl = findFirstAllowedUrl(menus);
    if (targetUrl) {
      handleNavigate(targetUrl);
    }
  };

  /* Accordion Logic: Auto-collapse other root items when one opens */
  /**
   * Update the menu open state
   * @param {any} items - Menu items
   * @param {any} id - Target ID
   * @param {any} depth - Recursion depth
   * @returns {any} Updated menu items
   */
  function updateMenuOpenState(items: MenuItem[], id: string, depth: number = 0): MenuItem[] {
    // Check if the target ID is a direct child of this list (to handle root exclusive toggle)
    const isTargetRoot = depth === 0 && items.some((item) => item.id === id);

    if (isTargetRoot) {
      return items.map((item) => ({
        ...item,
        isOpen: item.id === id ? !item.isOpen : false, // Close others if root
      }));
    }

    return items.map((item) => {
      if (item.id === id) {
        return { ...item, isOpen: !item.isOpen };
      }
      if (item.children) {
        return { ...item, children: updateMenuOpenState(item.children, id, depth + 1) };
      }
      return item;
    });
  }

  /**
   * Toggle submenu
   * @param {any} id - Menu item ID
   */
  function toggleSubMenu(id: string) {
    // Simply toggle the menu item state.
    // Floating popups will handle the display if sidebar is collapsed.
    setMenuItems((previous) => updateMenuOpenState(previous, id));
  }

  /**
   * Clear session tokens and navigate to credentials login page
   */
  const clearSessionAndRedirect = () => {
    removeLocalStorageItem('ev-access-token');
    removeLocalStorageItem('ev-refresh-token');
    removeLocalStorageItem('accountVerificationStatus');
    removeLocalStorageItem('activePortal');
    removeLocalStorageItem('userVerification');
    removeCookie('ev-access-token');
    removeCookie('ev-refresh-token');
    dispatch({ type: 'RESET_STORE' });
    navigate('/login/credentials');
  };

  /**
   * Logout function
   */
  const handleLogout = () => {
    // Clearing client name from local storage
    removeLocalStorageItem('client-name');

    authApi
      .logout({
        accessToken: getLocalStorageItem('ev-access-token') || '',
      })
      .then(() => {
        clearSessionAndRedirect();
      })
      .catch((error) => {
        console.error('Logout API failed, logging out locally anyway:', error);
        clearSessionAndRedirect();
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('logoutFailed'),
            code: error?.response?.data?.code,
            status: true,
            type: 'error',
          }),
        );
      });
  };

  /**
   * Check if route is active
   * @param itemUrl - URL to check
   * @param activeMatch - Custom match string
   * @returns True if active
   */
  const isRouteActive = React.useCallback(
    (itemUrl?: string, activeMatch?: string) => {
      const querySource = new URLSearchParams(location.search).get('source');
      const activePath = location.state?.source || querySource || location.pathname;

      if (activeMatch) {
        return activePath.includes(activeMatch);
      }
      if (!itemUrl) return false;
      return activePath.startsWith(itemUrl) && itemUrl !== '/ev-admin';
    },
    [location.pathname, location.state],
  );

  // Helper to check if any child is active
  /**
   * Check if parent is active
   * @param item - Menu item
   * @returns True if active
   */
  const isParentActive = React.useCallback(
    (item: MenuItem): boolean => {
      if (item.url && isRouteActive(item.url, item.activeMatch)) return true;
      if (item.children) {
        return item.children.some((child) => isParentActive(child));
      }
      return false;
    },
    [isRouteActive],
  );

  // Render a single menu item (recursive)
  /**
   * Render menu item
   * @param {any} item - Menu item
   * @param {any} depth - Depth
   * @returns {any} JSX element
   */
  function renderMenuItem(item: MenuItem, depth: number = 0) {
    const { allow, children, url, isOpen, id, label, svgIcon, activeSvgIcon, activeMatch } = item;
    if (!allow) return null;

    const hasChildren = children && children.length > 0;
    const isActive = isRouteActive(url, activeMatch);
    const isParent = hasChildren && isParentActive(item);

    return (
      <div key={id} className={`menu-item-container depth-${depth}`}>
        <div
          className={`sidebar-menu-item ${isActive ? 'active' : ''} ${isParent || (hasChildren && isOpen) ? 'parent-active' : ''}`}
          onClick={() => {
            if (hasChildren) {
              toggleSubMenu(id);
              if (!isOpen && children?.[0]?.url) {
                navigate(children[0].url);
              }
            } else {
              handleNavigate(url);
            }
          }}
          title={!expandSidebar && depth === 0 ? t(label) : ''}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              if (hasChildren) {
                toggleSubMenu(id);
                if (!isOpen && children?.[0]?.url) {
                  navigate(children[0].url);
                }
              } else {
                handleNavigate(url);
              }
            }
          }}>
          {depth === 0 && (
            <div className={`icon-container ${isActive || isParent ? 'active' : ''}`}>
              <span className="menu-icon">{isActive || isParent ? activeSvgIcon : svgIcon}</span>
            </div>
          )}

          {/* Show label if Expanded OR if we are in a floating popup (depth > 0) */}
          {(expandSidebar || depth > 0) && (
            <div className="label-container">
              <span className="menu-label">{label.includes(' ') ? label : t(label)}</span>
              {hasChildren && (
                <span className="chevron">
                  {isOpen ? (
                    <ChevronDown />
                  ) : (
                    <span style={{ transform: i18n.language === 'ar' ? 'rotate(180deg)' : 'none' }}>
                      <ChevronRight />
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Render children if open.
            Use 'submenu-container' for inline tree (expanded sidebar).
            Use 'floating-menu-container' for popup menu (collapsed sidebar or nested popup).
        */}
        {hasChildren && isOpen && (
          <div className={expandSidebar ? 'submenu-container' : 'floating-menu-container'}>
            {!expandSidebar && (
              <div className="floating-header">
                {svgIcon && <span className="header-icon">{svgIcon}</span>}
                {t(label)}
              </div>
            )}
            {children!.map((child) => renderMenuItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  useEffect(() => {
    /**
     * Open active menus
     * @param {any} items - Menu items
     * @returns {any} Updated menu items
     */
    const openActiveMenus = (items: MenuItem[]): MenuItem[] => {
      return items.map((item) => {
        const active = isParentActive(item);
        const newItem = { ...item };
        // Only auto-expand if sidebar is expanded.
        // If collapsed (floating mode), do NOT auto-open on route change.
        if (expandSidebar && active && item.children) {
          newItem.isOpen = true;
          newItem.children = openActiveMenus(item.children);
        } else if (item.children) {
          newItem.children = openActiveMenus(item.children);
        }
        return newItem;
      });
    };
    setMenuItems((previous) => openActiveMenus(previous));
  }, [location.pathname, expandSidebar, isParentActive]);

  /**
   * handleVersion fetches the current app version from version.txt
   */
  const handleVersion = async () => {
    try {
      // ✅ Automatically pick current domain
      const baseUrl = globalThis?.location?.origin;
      const url = `${baseUrl}/version.txt`;

      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const text = await response.text();

      // ❗ Avoid HTML error response
      if (!text || text.includes('<!DOCTYPE html>')) {
        setAppVersion('');
        return;
      }

      setAppVersion(text.trim());
    } catch (error) {
      console.error('Version fetch failed:', error);
      setAppVersion('');
    }
  };

  useEffect(() => {
    handleVersion();
  }, []);

  return (
    <div className="sidebar-wrap">
      <div
        className={`sidebar-container ${expandSidebar ? 'expand' : 'close'} ${i18n.language === 'ar' ? 'rtl' : ''}`}
        ref={sidebarReference}>
        <div className="sidebar-logo">
          <img src={brandImage.logo} alt="brand-logo" />
        </div>
        <div
          className="toggle-button"
          onClick={() => {
            if (expandSidebar) {
              setMenuItems((previous) => closeAllMenus(previous));
            }
            setExpandSidebar(!expandSidebar);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              if (expandSidebar) {
                setMenuItems((previous) => closeAllMenus(previous));
              }
              setExpandSidebar(!expandSidebar);
            }
          }}>
          {expandSidebar ? (
            <svg
              width="6"
              height="8"
              viewBox="0 0 6 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transform: i18n.language === 'ar' ? 'none' : 'rotate(180deg)',
                color: '#808080',
              }}>
              <path
                d="M1 9L5 5L1 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="6"
              height="8"
              viewBox="0 0 6 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transform: i18n.language === 'ar' ? 'rotate(180deg)' : 'none',
                color: '#808080',
              }}>
              <path
                d="M1 9L5 5L1 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>

        <div className="sidebar-scroll-area">{menuItems.map((item) => renderMenuItem(item))}</div>
        {portalType && (
          <div
            className={`bottom-portal-menu ${expandSidebar ? '' : 'bottom-portal-menu--collapsed'}`}>
            <div className="bottom-portal-menu__divider" />
            {allowedPortals.length > 0 && (
              <div
                className="bottom-portal-menu__grid"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(allowedPortals.length, 2)}, 1fr)`,
                }}>
                {allowedPortals.map((portal) => {
                  const isActive = activePortal === portal.id;

                  return (
                    <button
                      key={portal.id}
                      type="button"
                      className={`bottom-portal-menu__item ${isActive ? 'active' : ''}`}
                      onClick={() => handlePortalSwitch(portal.id)}>
                      <div className="bottom-portal-menu__icon">
                        {isActive ? portal.activeSvgIcon : portal.svgIcon}
                      </div>

                      <span className="bottom-portal-menu__label">{t(portal.label)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div className="sidebar-footer">
          <div
            className="sidebar-logout"
            role="button"
            tabIndex={0}
            onClick={handleLogout}
            onKeyDown={(event) => event.key === 'Enter' && handleLogout()}>
            <span className="logout-icon">
              <LogoutIcon />
            </span>
            {expandSidebar && <span className="logout-text">{t('logout')}</span>}
          </div>
          {expandSidebar && (
            <div className="app-version">
              <span>{appVersion}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
